import { normalizeNonBreakingSpaces } from 'src/lib/rich-text-editor/normalizeNonBreakingSpaces';
import { SpeakUpReportedFieldsValue } from '../types';

const DEFAULT_LOCALE = 'en-GB';

export interface CategoryFieldsEntitlements {
  goTogetherEnabled: boolean;
  vaultTalkEntitled: boolean;
  slackEntitled: boolean;
  reportingPortalEntitled: boolean;
}

export interface BuildCategoryFieldsParams {
  name: string;
  isDetermined: boolean;
  isReported: boolean;
  reportedFields: SpeakUpReportedFieldsValue;
  entitlements: CategoryFieldsEntitlements;
  /** Existing description to preserve on edit. Defaults to '' (create). */
  existingDescription?: string;
  /** Legacy layout passes manualIntake and vaultApp as separate toggles. */
  legacyOverrides?: { manualIntake?: boolean; vaultApp?: boolean };
}

export interface CategoryFieldsPayload {
  isDeterminedCategory: boolean;
  isReportedCategory: boolean;
  question: boolean;
  allowGoTogether: boolean;
  displayInApp: boolean;
  displayInManualIntake: boolean;
  displayInOpenReporting: boolean;
  displayInVaultTalk: boolean;
  displayInCollaborationToolIntegration: boolean;
  requestOpenPerpetrators: boolean;
  locales: Record<string, { name: string; description: string }>;
  isCustomQuestions: boolean;
}

export const buildCategoryFieldsPayload = ({
  name,
  isDetermined,
  isReported,
  reportedFields,
  entitlements,
  existingDescription = '',
  legacyOverrides,
}: BuildCategoryFieldsParams): CategoryFieldsPayload => {
  const {
    goTogetherEnabled,
    vaultTalkEntitled,
    slackEntitled,
    reportingPortalEntitled,
  } = entitlements;

  const isCustomFlow = isReported && reportedFields.questionFlow === 'custom';

  const locales: Record<string, { name: string; description: string }> =
    name.trim()
      ? {
          [DEFAULT_LOCALE]: {
            name: name.trim(),
            description: normalizeNonBreakingSpaces(existingDescription),
          },
        }
      : {};

  return {
    isDeterminedCategory: isDetermined,
    isReportedCategory: isReported,
    question:
      isReported && !isCustomFlow
        ? reportedFields.reportType === 'inquiry'
        : false,
    allowGoTogether:
      isReported && goTogetherEnabled && !isCustomFlow
        ? reportedFields.goTogether
        : false,
    displayInApp: legacyOverrides
      ? (legacyOverrides.vaultApp ?? isReported)
      : isReported,
    displayInManualIntake: legacyOverrides
      ? (legacyOverrides.manualIntake ?? isReported)
      : isReported,
    displayInOpenReporting: isReported
      ? isCustomFlow ||
        (reportedFields.channels.reportingPortal && reportingPortalEntitled)
      : false,
    displayInVaultTalk:
      isCustomFlow || !isReported
        ? false
        : reportedFields.channels.vaultTalk && vaultTalkEntitled,
    displayInCollaborationToolIntegration:
      isCustomFlow || !isReported
        ? false
        : reportedFields.channels.slack && slackEntitled,
    requestOpenPerpetrators: false,
    locales,
    isCustomQuestions: isCustomFlow,
  };
};
